import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  reportTemplates, reportSections, listReports, saveReport, deleteReport, runReport, listRuns, deleteRun,
  listSchedules, saveSchedule, deleteSchedule, listShares, shareRun, revokeShare, openHtml,
} from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import RequirePermission from '../auth/RequirePermission';

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const cfgOf = (r) => { try { return JSON.parse(r.configJson || '{}'); } catch { return {}; } };

export default function ReportsPage() {
  const { sites } = useActiveSite();
  const [tab, setTab] = useState('reports');
  const [templates, setTemplates] = useState([]);
  const [sections, setSections] = useState([]);
  const [reports, setReports] = useState([]);
  const [runs, setRuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState(null); // {id?, name, description, cfg:{siteId,days,title,footer,sections[]}}
  const [sched, setSched] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    try {
      const [t, s, r, ru, sc, sh] = await Promise.all([reportTemplates(), reportSections(), listReports(), listRuns(), listSchedules(), listShares()]);
      setTemplates(t); setSections(s); setReports(r); setRuns(ru); setSchedules(sc); setShares(sh);
    } catch (e) { setError(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setError(''); setInfo('');
    try { const r = await fn(); if (ok) setInfo(ok); await load(); return r; } catch (e) { setError(errorMessage(e)); return null; }
  };
  const grouped = useMemo(() => {
    const m = new Map();
    sections.forEach((s) => { if (!m.has(s.group)) m.set(s.group, []); m.get(s.group).push(s); });
    return [...m.entries()];
  }, [sections]);

  const newFrom = (t) => setForm({ name: t ? t.name : '', description: t ? t.description : '', cfg: { siteId: '', days: t ? t.days : 7, title: '', footer: '', sections: t ? [...t.sections] : [] } });
  const toggle = (key) => setForm((f) => ({ ...f, cfg: { ...f.cfg, sections: f.cfg.sections.includes(key) ? f.cfg.sections.filter((k) => k !== key) : [...f.cfg.sections, key] } }));
  const move = (i, d) => setForm((f) => {
    const arr = [...f.cfg.sections]; const j = i + d; if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, cfg: { ...f.cfg, sections: arr } };
  });
  const titleOf = (k) => sections.find((s) => s.key === k)?.title || k;

  const submit = async (e) => {
    e.preventDefault();
    const ok = await act(() => saveReport(form.id, { name: form.name, description: form.description, configJson: JSON.stringify(form.cfg) }), 'Report saved.');
    if (ok) setForm(null);
  };
  const run = async (id) => {
    const r = await act(() => runReport(id), 'Report generated.');
    if (r) openHtml(`/reports/runs/${r.id}/html`).catch((e) => setError(errorMessage(e)));
  };
  const copyLink = (token) => {
    const url = `${window.location.origin}/api/public/share/${token}`;
    navigator.clipboard?.writeText(url); setInfo('Link copied: ' + url);
  };

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Reports</h1>
        <p className="muted">Pick sections, generate a printable report, e-mail it on a schedule or share a link.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}
      <div className="mod-tabs">
        {[['reports', `Reports (${reports.length})`], ['runs', `Generated (${runs.length})`], ['schedules', `Schedules (${schedules.length})`], ['shares', `Shared links (${shares.length})`]].map(([k, l]) =>
          <button key={k} className={tab === k ? 'primary' : 'ghost'} onClick={() => setTab(k)}>{l}</button>)}
      </div>

      {tab === 'reports' && !form && (
        <>
          <RequirePermission code="workspace.write">
            <div className="sec-card" style={{ marginBottom: 16 }}>
              <h3>Start from a template</h3>
              <div className="mod-grid">
                {templates.map((t) => (
                  <button key={t.key} className="mod-tile" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => newFrom(t)}>
                    <b>{t.name}</b><small>{t.description}</small></button>))}
                <button className="mod-tile" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => newFrom(null)}><b>Blank report</b><small>Choose your own sections.</small></button>
              </div>
            </div>
          </RequirePermission>
          <div className="sec-card sec-scroll">
            <table className="sec-table">
              <thead><tr><th>Name</th><th>Sections</th><th>Period</th><th /></tr></thead>
              <tbody>
                {reports.map((r) => { const c = cfgOf(r); return (
                  <tr key={r.id}><td><b>{r.name}</b><div className="muted">{r.description}</div></td><td>{(c.sections || []).length}</td><td>{c.days || 7} days</td>
                    <td><RequirePermission code="workspace.write"><div className="sec-row">
                      <button className="primary" onClick={() => run(r.id)}>Generate</button>
                      <button className="ghost" onClick={() => setForm({ id: r.id, name: r.name, description: r.description, cfg: { siteId: '', days: 7, title: '', footer: '', sections: [], ...c } })}>Edit</button>
                      <button className="ghost" onClick={() => setSched({ reportId: r.id, frequency: 'Weekly', hourUtc: 6, dayOfWeek: 1, dayOfMonth: 1, recipients: '', isEnabled: true })}>Schedule</button>
                      <button className="ghost danger" onClick={() => window.confirm('Delete this report and its schedules?') && act(() => deleteReport(r.id))}>Delete</button>
                    </div></RequirePermission></td></tr>); })}
                {reports.length === 0 && <tr><td colSpan="4" className="muted">No reports yet. Start from a template above.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'reports' && form && (
        <form className="sec-card mod-form" style={{ maxWidth: 900 }} onSubmit={submit}>
          <h3>{form.id ? 'Edit report' : 'New report'}</h3>
          <label>Name<input className="sec-input" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Description<input className="sec-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <label>Heading on the report (optional)<input className="sec-input" value={form.cfg.title} onChange={(e) => setForm({ ...form, cfg: { ...form.cfg, title: e.target.value } })} /></label>
          <div className="sec-row">
            <label>Site<SiteSelector sites={sites} value={form.cfg.siteId || ''} onChange={(v) => setForm({ ...form, cfg: { ...form.cfg, siteId: v } })} /></label>
            <label>Days<select className="sec-select" value={form.cfg.days} onChange={(e) => setForm({ ...form, cfg: { ...form.cfg, days: Number(e.target.value) } })}>
              {[1, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d}</option>)}</select></label>
          </div>
          <label>Footer text (optional)<input className="sec-input" value={form.cfg.footer} onChange={(e) => setForm({ ...form, cfg: { ...form.cfg, footer: e.target.value } })} /></label>

          <div>
            <b>Order of sections ({form.cfg.sections.length})</b>
            {form.cfg.sections.map((k, i) => (
              <div key={k} className="sec-row" style={{ margin: '4px 0' }}>
                <span style={{ flex: 1 }}>{i + 1}. {titleOf(k)}</span>
                <button type="button" className="ghost" onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="ghost" onClick={() => move(i, 1)}>↓</button>
                <button type="button" className="ghost danger" onClick={() => toggle(k)}>Remove</button>
              </div>))}
          </div>
          <div>
            <b>Add sections</b>
            {grouped.map(([g, list]) => (
              <div key={g} style={{ margin: '8px 0' }}>
                <div className="muted" style={{ fontSize: 12 }}>{g}</div>
                {list.map((s) => (
                  <label key={s.key} style={{ display: 'inline-block', marginRight: 14 }}>
                    <input type="checkbox" checked={form.cfg.sections.includes(s.key)} onChange={() => toggle(s.key)} /> {s.title}</label>))}
              </div>))}
          </div>
          <div className="sec-row"><button className="primary" type="submit">Save report</button><button className="ghost" type="button" onClick={() => setForm(null)}>Cancel</button></div>
        </form>
      )}

      {tab === 'runs' && (
        <div className="sec-card sec-scroll">
          <table className="sec-table">
            <thead><tr><th>Title</th><th>Created</th><th>Trigger</th><th>E-mailed</th><th /></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}><td><b>{r.title}</b></td><td>{new Date(r.createdAt).toLocaleString()}</td><td>{r.trigger}</td><td>{r.emailed ? 'Yes' : 'No'}</td>
                  <td><div className="sec-row">
                    <button className="ghost" onClick={() => openHtml(`/reports/runs/${r.id}/html`).catch((e) => setError(errorMessage(e)))}>Open / print</button>
                    <RequirePermission code="workspace.write">
                      <button className="ghost" onClick={async () => { const l = await act(() => shareRun(r.id, 30), 'Share link created (valid 30 days).'); if (l) copyLink(l.token); }}>Share link</button>
                      <button className="ghost danger" onClick={() => window.confirm('Delete?') && act(() => deleteRun(r.id))}>Delete</button>
                    </RequirePermission>
                  </div></td></tr>))}
              {runs.length === 0 && <tr><td colSpan="5" className="muted">Nothing generated yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'schedules' && (
        <div className="sec-card sec-scroll">
          <table className="sec-table">
            <thead><tr><th>Report</th><th>When</th><th>Recipients</th><th>Next run</th><th>On</th><th /></tr></thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.id}><td>{reports.find((r) => r.id === s.reportId)?.name || '—'}</td>
                  <td>{s.frequency}{s.frequency === 'Weekly' ? ` · ${DOW[s.dayOfWeek]}` : s.frequency === 'Monthly' ? ` · day ${s.dayOfMonth}` : ''} · {String(s.hourUtc).padStart(2, '0')}:00 UTC</td>
                  <td>{s.recipients}</td><td>{s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : '—'}</td><td>{s.isEnabled ? 'Yes' : 'No'}</td>
                  <td><RequirePermission code="workspace.write"><div className="sec-row">
                    <button className="ghost" onClick={() => setSched({ ...s })}>Edit</button>
                    <button className="ghost danger" onClick={() => window.confirm('Delete schedule?') && act(() => deleteSchedule(s.id))}>Delete</button></div></RequirePermission></td></tr>))}
              {schedules.length === 0 && <tr><td colSpan="6" className="muted">No schedules. Use “Schedule” on a report. Requires e-mail (SMTP) to be configured on the server.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'shares' && (
        <div className="sec-card sec-scroll">
          <table className="sec-table">
            <thead><tr><th>Created</th><th>Expires</th><th>Views</th><th>Status</th><th /></tr></thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.id}><td>{new Date(s.createdAt).toLocaleString()}</td><td>{s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : 'Never'}</td><td>{s.viewCount}</td><td>{s.isRevoked ? 'Revoked' : 'Active'}</td>
                  <td><div className="sec-row">
                    {!s.isRevoked && <button className="ghost" onClick={() => copyLink(s.token)}>Copy link</button>}
                    {!s.isRevoked && <RequirePermission code="workspace.write"><button className="ghost danger" onClick={() => act(() => revokeShare(s.id), 'Link revoked.')}>Revoke</button></RequirePermission>}
                  </div></td></tr>))}
              {shares.length === 0 && <tr><td colSpan="5" className="muted">No shared links. Anyone with a link can view that snapshot, so share carefully. Links can be embedded in an iframe.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {sched && (
        <div className="sec-drawer-bg" onClick={() => setSched(null)}>
          <form className="sec-drawer mod-form" onClick={(e) => e.stopPropagation()} onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act(() => saveSchedule(sched.id, { ...sched, hourUtc: Number(sched.hourUtc), dayOfWeek: Number(sched.dayOfWeek), dayOfMonth: Number(sched.dayOfMonth) }), 'Schedule saved.');
            if (ok) setSched(null);
          }}>
            <h3>E-mail schedule</h3>
            <label>Frequency<select className="sec-select" value={sched.frequency} onChange={(e) => setSched({ ...sched, frequency: e.target.value })}>
              {['Daily', 'Weekly', 'Monthly'].map((f) => <option key={f}>{f}</option>)}</select></label>
            {sched.frequency === 'Weekly' && <label>Day<select className="sec-select" value={sched.dayOfWeek} onChange={(e) => setSched({ ...sched, dayOfWeek: e.target.value })}>
              {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></label>}
            {sched.frequency === 'Monthly' && <label>Day of month (1-28)<input className="sec-input" type="number" min="1" max="28" value={sched.dayOfMonth} onChange={(e) => setSched({ ...sched, dayOfMonth: e.target.value })} /></label>}
            <label>Hour (UTC)<input className="sec-input" type="number" min="0" max="23" value={sched.hourUtc} onChange={(e) => setSched({ ...sched, hourUtc: e.target.value })} /></label>
            <label>Recipients (comma separated)<input className="sec-input" required value={sched.recipients} onChange={(e) => setSched({ ...sched, recipients: e.target.value })} placeholder="a@example.com, b@example.com" /></label>
            <label style={{ display: 'block' }}><input type="checkbox" checked={sched.isEnabled} onChange={(e) => setSched({ ...sched, isEnabled: e.target.checked })} /> Enabled</label>
            <div className="sec-row"><button className="primary" type="submit">Save</button><button className="ghost" type="button" onClick={() => setSched(null)}>Cancel</button></div>
          </form>
        </div>
      )}
    </div>
  );
}
